import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type HTMLAttributes,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import InlineSVG from "react-inlinesvg";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useQueryClient } from "@tanstack/react-query";
import { cn } from "src/lib/cn";
import { WithReset } from "src/sudokit/Layout/BoxReset";
import { Switch } from "src/sudokit/components/Switch";
import IconButton from "src/components/IconButton";
import { usePersistentChatUserSettings } from "../../PersistentChat/PersistentChatUserSettings";
import {
  MEMORIES_QUERY_KEY,
  useMemories,
  type Memory,
} from "../../PersistentChat/ChatPersonalization/useMemories";
import {
  formatLanguagePreferenceDisplayText,
  parseResponseLanguagePreferenceMemory,
} from "../../PersistentChat/ChatPersonalization/languagePreferenceMemory";
import {
  CUSTOM_INSTRUCTIONS_MAX_WORDS,
  RESPONSE_LENGTH_OPTIONS,
  TONE_OPTIONS,
  hasAdvancedPersonalizationContent,
  type ChatResponseLength,
  type ChatTone,
} from "~shared/chatPersonalization";

function countWords(text: string): number {
  return text.trim().split(/\s+/).filter(Boolean).length;
}

function formatMemoryContentForDisplay(content: string): string {
  const normalized = content.trim().toLowerCase();
  const responseLanguage = parseResponseLanguagePreferenceMemory(content);
  if (responseLanguage) {
    return formatLanguagePreferenceDisplayText(responseLanguage);
  }

  if (normalized === "[agentic_pref] suggestion_insert=always_insert") {
    return "Always insert suggestions into my document.";
  }

  if (normalized === "[agentic_pref] suggestion_insert=ask_before_insert") {
    return "Ask before inserting suggestions into my document.";
  }

  if (normalized === "[agentic_pref] document_open=always_open") {
    return "Always open newly created documents.";
  }

  if (normalized === "[agentic_pref] document_open=always_closed") {
    return "Do not automatically open newly created documents.";
  }

  return content;
}

export interface ChatPersonalizationSectionProps
  extends Omit<HTMLAttributes<HTMLElement>, "title"> {
  /** Whether the host surface is currently active. Activation refreshes memories. */
  active?: boolean;
  /** Expand Advanced for an explicit deep link, even when no advanced content exists. */
  defaultAdvancedOpen?: boolean;
  /** Optional action rendered alongside the section title, such as a dialog close button. */
  headerAction?: ReactNode;
}

export function ChatPersonalizationSection({
  active = true,
  defaultAdvancedOpen = false,
  headerAction,
  className,
  ...props
}: ChatPersonalizationSectionProps) {
  const queryClient = useQueryClient();
  const { personalization } = usePersistentChatUserSettings();
  const {
    memories,
    isLoading: isLoadingMemories,
    deleteMemory,
    deleteAllMemories,
  } = useMemories();
  const [manualAdvancedOpen, setManualAdvancedOpen] = useState<boolean | null>(null);
  const [localInstructions, setLocalInstructions] = useState(
    personalization.chatCustomInstructions,
  );
  const [localInstructionsDirty, setLocalInstructionsDirty] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const savingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(false);
  const previousActiveRef = useRef(false);
  const previousAdvancedOpenRef = useRef(false);
  const localInstructionsRef = useRef(localInstructions);
  const localInstructionsDirtyRef = useRef(localInstructionsDirty);
  const personalizationRef = useRef(personalization);
  localInstructionsRef.current = localInstructions;
  localInstructionsDirtyRef.current = localInstructionsDirty;
  personalizationRef.current = personalization;

  const effectiveCustomInstructions = localInstructionsDirty
    ? localInstructions
    : personalization.chatCustomInstructions;
  const shouldDefaultAdvancedOpen =
    defaultAdvancedOpen ||
    hasAdvancedPersonalizationContent({
      memoriesCount: memories.length,
      customInstructions: effectiveCustomInstructions,
    });
  const advancedOpen =
    active && (manualAdvancedOpen ?? shouldDefaultAdvancedOpen);
  const animateAdvancedSection = manualAdvancedOpen !== null;

  // A new advanced deep link must override a prior manual collapse. Latch open
  // so clearing styleSettingsFocusAdvanced afterward does not immediately close
  // Advanced when there is no advanced content yet.
  useEffect(() => {
    if (!active || !defaultAdvancedOpen) return;
    setManualAdvancedOpen(true);
  }, [active, defaultAdvancedOpen]);

  const triggerSaving = useCallback(() => {
    if (savingTimerRef.current) clearTimeout(savingTimerRef.current);
    setIsSaving(true);
    savingTimerRef.current = setTimeout(() => setIsSaving(false), 1500);
  }, []);

  const persistLocalInstructionsIfDirty = useCallback(
    (showSavingIndicator = true) => {
      if (!localInstructionsDirtyRef.current) return;

      const current = localInstructionsRef.current;
      const currentPersonalization = personalizationRef.current;
      if (current !== currentPersonalization.chatCustomInstructions) {
        currentPersonalization.setChatCustomInstructions(current);
        if (showSavingIndicator && mountedRef.current) triggerSaving();
      }

      localInstructionsDirtyRef.current = false;
      if (mountedRef.current) setLocalInstructionsDirty(false);
    },
    [triggerSaving],
  );

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (savingTimerRef.current) clearTimeout(savingTimerRef.current);
      persistLocalInstructionsIfDirty(false);
    };
  }, [persistLocalInstructionsIfDirty]);

  useEffect(() => {
    const wasActive = previousActiveRef.current;
    previousActiveRef.current = active;

    if (!active) {
      if (wasActive) persistLocalInstructionsIfDirty(false);
      setManualAdvancedOpen(null);
      return;
    }

    if (!wasActive) {
      void queryClient.refetchQueries({ queryKey: MEMORIES_QUERY_KEY });
      const storedInstructions = personalization.chatCustomInstructions;
      setLocalInstructions(storedInstructions);
      localInstructionsRef.current = storedInstructions;
      setLocalInstructionsDirty(false);
      localInstructionsDirtyRef.current = false;
      return;
    }

    if (!localInstructionsDirtyRef.current) {
      setLocalInstructions(personalization.chatCustomInstructions);
      localInstructionsRef.current = personalization.chatCustomInstructions;
    }
  }, [
    active,
    personalization.chatCustomInstructions,
    persistLocalInstructionsIfDirty,
    queryClient,
  ]);

  useEffect(() => {
    if (previousAdvancedOpenRef.current && !advancedOpen) {
      persistLocalInstructionsIfDirty();
    }
    previousAdvancedOpenRef.current = advancedOpen;
  }, [advancedOpen, persistLocalInstructionsIfDirty]);

  const handleInstructionsChange = (value: string) => {
    localInstructionsRef.current = value;
    localInstructionsDirtyRef.current = true;
    setLocalInstructions(value);
    setLocalInstructionsDirty(true);
  };

  const handleDeleteAll = () => {
    deleteAllMemories.mutate();
    setShowDeleteConfirm(false);
  };

  const wordCount = countWords(localInstructions);
  const advancedSectionContent = (
    <AdvancedSectionContent
      personalization={personalization}
      memories={memories}
      isLoadingMemories={isLoadingMemories}
      localInstructions={localInstructions}
      wordCount={wordCount}
      onInstructionsChange={handleInstructionsChange}
      onInstructionsBlur={persistLocalInstructionsIfDirty}
      onDeleteMemory={(id) => deleteMemory.mutate(id)}
      onDeleteAllMemories={() => setShowDeleteConfirm(true)}
      triggerSaving={triggerSaving}
    />
  );

  return (
    <>
      <section
        className={cn("flex flex-col text-left", className)}
        data-section="chat-personalization"
        {...props}
      >
        <div className="flex items-center justify-between mb-7">
          <div className="flex items-center gap-2.5">
            <InlineSVG
              src="/assets/chat-personalization-icon.svg"
              className="w-7 h-7 [&_path]:fill-app-grey-0"
              aria-hidden="true"
            />
            <h2 className="m-0 text-xl font-medium font-inter text-app-grey-0">
              Chat Personalization
            </h2>
            <AnimatePresence>
              {isSaving && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  transition={{ duration: 0.15 }}
                  role="status"
                  aria-label="Saving chat personalization"
                >
                  <InlineSVG
                    src="/assets/loader.svg"
                    className="w-4 h-4 animate-spin [&_path]:fill-app-grey-1"
                    aria-hidden="true"
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          {headerAction}
        </div>

        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-2.5">
            <span className="text-sm font-semibold font-inter text-app-grey-0">
              Tone
            </span>
            <RadioCardGroup
              options={TONE_OPTIONS}
              value={personalization.chatTone}
              onChange={(value) => {
                personalization.setChatTone(value as ChatTone);
                triggerSaving();
              }}
              ariaLabel="Tone"
            />
          </div>

          <div className="flex flex-col gap-2.5">
            <span className="text-sm font-semibold font-inter text-app-grey-0">
              Response Length
            </span>
            <RadioCardGroup
              options={RESPONSE_LENGTH_OPTIONS}
              value={personalization.chatResponseLength}
              onChange={(value) => {
                personalization.setChatResponseLength(
                  value as ChatResponseLength,
                );
                triggerSaving();
              }}
              ariaLabel="Response Length"
            />
          </div>

          <button
            type="button"
            onClick={() => setManualAdvancedOpen(!advancedOpen)}
            className="flex items-center gap-1.5 cursor-pointer p-0"
            data-testid="advanced-toggle"
            aria-expanded={advancedOpen}
          >
            <InlineSVG
              src="/assets/chevron-right.svg"
              className={cn(
                "w-5 h-5 [&_path]:fill-app-grey-0 transition-transform duration-200",
                advancedOpen && "rotate-90",
              )}
              aria-hidden="true"
            />
            <span className="text-sm font-semibold font-inter text-app-grey-0">
              Advanced
            </span>
          </button>

          {animateAdvancedSection ? (
            <AnimatePresence>
              {advancedOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{
                    height: "auto",
                    opacity: 1,
                    transition: {
                      type: "spring",
                      stiffness: 300,
                      damping: 24,
                    },
                  }}
                  exit={{
                    height: 0,
                    opacity: 0,
                    transition: {
                      duration: 0.2,
                      ease: [0.4, 0, 0.2, 1],
                    },
                  }}
                  className="overflow-hidden -mt-2"
                >
                  {advancedSectionContent}
                </motion.div>
              )}
            </AnimatePresence>
          ) : (
            advancedOpen && (
              <div className="overflow-hidden -mt-2">
                {advancedSectionContent}
              </div>
            )
          )}
        </div>
      </section>

      <DialogPrimitive.Root
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
      >
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay
            className={cn(
              "fixed inset-0 z-[9999999999] bg-black/50 backdrop-blur-[2px]",
              "flex items-center justify-center",
              "data-[state=open]:animate-in data-[state=closed]:animate-out",
              "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
              "duration-200",
            )}
          >
            <DialogPrimitive.Content asChild>
              <WithReset
                boxOnly
                className={cn(
                  "w-full max-w-[400px] text-left",
                  "bg-app-menu-color rounded-xl",
                  "!border !border-solid !border-app-grey-3",
                  "p-6 shadow-card",
                  "data-[state=open]:animate-in data-[state=closed]:animate-out",
                  "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
                  "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
                  "duration-200",
                )}
                onClick={(event: any) => event.stopPropagation()}
              >
                <span className="text-lg font-semibold font-inter text-app-grey-0 block mb-2">
                  Delete All Memories
                </span>
                <span className="text-sm font-inter text-app-grey-1 block mb-5">
                  Are you sure? This will permanently delete chat preferences
                  that Sudowrite has learned.
                </span>
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setShowDeleteConfirm(false)}
                    className="px-4 py-2 rounded-lg !border !border-solid !border-app-grey-3 text-sm font-inter text-app-grey-0 cursor-pointer hover:bg-app-grey-4 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleDeleteAll}
                    className="px-4 py-2 rounded-lg bg-red-500 text-sm font-inter text-white cursor-pointer hover:bg-red-600 transition-colors"
                    data-testid="confirm-delete-all"
                  >
                    Delete All Memories
                  </button>
                </div>
              </WithReset>
            </DialogPrimitive.Content>
          </DialogPrimitive.Overlay>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
    </>
  );
}

type AdvancedSectionContentProps = {
  personalization: ReturnType<
    typeof usePersistentChatUserSettings
  >["personalization"];
  memories: Memory[];
  isLoadingMemories: boolean;
  localInstructions: string;
  wordCount: number;
  onInstructionsChange: (value: string) => void;
  onInstructionsBlur: () => void;
  onDeleteMemory: (id: string) => void;
  onDeleteAllMemories: () => void;
  triggerSaving: () => void;
};

const AdvancedSectionContent = ({
  personalization,
  memories,
  isLoadingMemories,
  localInstructions,
  wordCount,
  onInstructionsChange,
  onInstructionsBlur,
  onDeleteMemory,
  onDeleteAllMemories,
  triggerSaving,
}: AdvancedSectionContentProps) => (
  <div className="flex flex-col">
    <div className="flex items-center justify-between gap-3">
      <span className="text-sm font-semibold font-inter text-app-grey-0">
        Allow Sudowrite to remember your preferences
      </span>
      <div className="flex items-center gap-3 flex-shrink-0">
        <Switch
          checked={personalization.chatMemoryEnabled}
          onCheckedChange={(value) => {
            personalization.setChatMemoryEnabled(value);
            triggerSaving();
          }}
          aria-label="Allow Sudowrite to remember your preferences"
          data-testid="memory-toggle"
        />
        <OverflowMenu
          disabled={memories.length === 0}
          onDeleteAll={onDeleteAllMemories}
        />
      </div>
    </div>

    {personalization.chatMemoryEnabled && (
      <div className="mt-2.5">
        <MemorySection
          memories={memories}
          isLoading={isLoadingMemories}
          onDelete={onDeleteMemory}
        />
      </div>
    )}

    <div className="flex flex-col gap-2.5 mt-5">
      <span className="text-sm font-semibold font-inter text-app-grey-0">
        Custom Instructions
      </span>
      <textarea
        aria-label="Custom Instructions"
        value={localInstructions}
        onChange={(event) => onInstructionsChange(event.target.value)}
        onBlur={() => onInstructionsBlur()}
        placeholder="What's your preferred tone? How do you like to receive feedback?"
        rows={5}
        className={cn(
          "w-full resize-none rounded-lg",
          "!border !border-solid !border-app-grey-3",
          "bg-app-document-background p-3 text-sm font-inter text-app-grey-0",
          "placeholder:text-app-grey-2",
          "focus:outline-none focus:!border-app-primary-cta-high-contrast transition-colors",
        )}
      />
      <div className="flex justify-between">
        <span className="text-xs font-inter text-app-grey-1">
          This affects chat responses only
        </span>
        <span
          className={cn(
            "text-xs font-inter",
            wordCount > CUSTOM_INSTRUCTIONS_MAX_WORDS
              ? "text-red-500"
              : "text-app-grey-1",
          )}
        >
          {wordCount}/{CUSTOM_INSTRUCTIONS_MAX_WORDS} words
        </span>
      </div>
    </div>
  </div>
);

type RadioCardGroupProps = {
  options: Array<{ value: string; label: string; description: string }>;
  value: string;
  onChange: (value: string) => void;
  ariaLabel: string;
};

const RadioCardGroup = ({
  options,
  value,
  onChange,
  ariaLabel,
}: RadioCardGroupProps) => (
  <motion.div
    whileTap={{ scale: 0.99 }}
    transition={{ type: "spring", stiffness: 400, damping: 17 }}
    className="flex rounded-lg !border !border-solid !border-app-grey-3 overflow-hidden"
    role="radiogroup"
    aria-label={ariaLabel}
  >
    {options.map((option, index) => {
      const isSelected = value === option.value;
      return (
        <button
          type="button"
          key={option.value}
          role="radio"
          aria-checked={isSelected}
          onClick={() => onChange(option.value)}
          data-testid={`radio-${option.value}`}
          data-selected={isSelected}
          className={cn(
            "flex-1 flex items-center gap-2.5 px-3 py-3 cursor-pointer text-left transition-colors",
            index < options.length - 1 &&
              "!border-r !border-solid !border-app-grey-3",
            isSelected
              ? "bg-app-document-background"
              : "hover:bg-app-primary-cta-transparent-05",
          )}
        >
          <div
            className={cn(
              "w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0",
              isSelected
                ? "!border-[1.5px] !border-solid !border-app-primary-cta-high-contrast"
                : "!border-[1.5px] !border-solid !border-app-grey-2",
            )}
          >
            {isSelected && (
              <div className="w-2.5 h-2.5 rounded-full bg-app-primary-cta-high-contrast" />
            )}
          </div>
          <div className="flex flex-col gap-0.5 min-w-0">
            <span
              className={cn(
                "text-sm font-medium font-inter whitespace-nowrap",
                isSelected
                  ? "text-app-primary-cta-high-contrast"
                  : "text-app-grey-0",
              )}
            >
              {option.label}
            </span>
            <span
              className={cn(
                "text-xs font-inter leading-tight",
                isSelected
                  ? "text-app-primary-cta-high-contrast"
                  : "text-app-grey-1",
              )}
            >
              {option.description}
            </span>
          </div>
        </button>
      );
    })}
  </motion.div>
);

const OverflowMenu = ({
  disabled,
  onDeleteAll,
}: {
  disabled: boolean;
  onDeleteAll: () => void;
}) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setPosition({ top: rect.bottom + 4, left: rect.left });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleOutsideMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", handleOutsideMouseDown);
    return () =>
      document.removeEventListener("mousedown", handleOutsideMouseDown);
  }, [open]);

  return (
    <div ref={triggerRef}>
      <IconButton
        src="/assets/three-dots.svg"
        size={32}
        iconSize={18}
        aria-label="Memory options"
        onClick={() => setOpen((value) => !value)}
        className="[&_path]:fill-app-grey-1"
      />
      {createPortal(
        <AnimatePresence>
          {open && (
            <motion.div
              ref={menuRef}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{
                duration: 0.15,
                ease: [0.16, 1, 0.3, 1],
              }}
              className="fixed origin-top-left"
              style={{
                top: position.top,
                left: position.left,
                zIndex: 2147483647,
              }}
            >
              <WithReset
                boxOnly
                className="bg-app-menu-color !border !border-solid !border-app-menu-color-hover rounded-xl shadow-card p-1.5 min-w-[210px]"
              >
                <button
                  type="button"
                  data-testid="menu-item-delete-all"
                  disabled={disabled}
                  onClick={() => {
                    if (!disabled) {
                      onDeleteAll();
                      setOpen(false);
                    }
                  }}
                  className={cn(
                    "w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-inter font-medium cursor-pointer transition-colors text-left",
                    disabled
                      ? "text-app-grey-2 cursor-not-allowed"
                      : "text-red-500 hover:bg-app-menu-color-hover",
                  )}
                >
                  <InlineSVG
                    src="/assets/delete.svg"
                    className={cn(
                      "w-4 h-4",
                      disabled
                        ? "[&_path]:fill-app-grey-2"
                        : "[&_path]:fill-red-500",
                    )}
                    aria-hidden="true"
                  />
                  Delete All Memories
                </button>
              </WithReset>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </div>
  );
};

const MemorySection = ({
  memories,
  isLoading,
  onDelete,
}: {
  memories: Memory[];
  isLoading: boolean;
  onDelete: (id: string) => void;
}) => {
  if (isLoading && memories.length === 0) {
    return (
      <div
        className="flex flex-col items-center justify-center gap-3 py-8 rounded-lg !border !border-solid !border-app-grey-3 bg-app-document-background"
        aria-busy="true"
      >
        <InlineSVG
          src="/assets/chat-personalization-icon.svg"
          className="w-7 h-7 opacity-0"
          aria-hidden="true"
        />
        <span
          className="text-sm font-inter text-transparent text-center select-none"
          aria-hidden="true"
        >
          Keep chatting to help Sudowrite understand your preferences
        </span>
      </div>
    );
  }

  if (memories.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-8 rounded-lg !border !border-solid !border-app-grey-3 bg-app-document-background">
        <InlineSVG
          src="/assets/chat-personalization-icon.svg"
          className="w-7 h-7 [&_path]:fill-app-grey-1"
          aria-hidden="true"
        />
        <span className="text-sm font-inter text-app-grey-1 text-center">
          Keep chatting to help Sudowrite understand your preferences
        </span>
      </div>
    );
  }

  return (
    <div className="flex flex-col rounded-lg !border !border-solid !border-app-grey-3 overflow-hidden">
      {memories.map((memory, index) => (
        <MemoryItem
          key={memory.id}
          memory={memory}
          onDelete={() => onDelete(memory.id)}
          isLast={index === memories.length - 1}
        />
      ))}
    </div>
  );
};

const MemoryItem = ({
  memory,
  onDelete,
  isLast,
}: {
  memory: Memory;
  onDelete: () => void;
  isLast: boolean;
}) => {
  const displayText = formatMemoryContentForDisplay(memory.content);

  return (
    <div
      className={cn(
        "group flex items-center justify-between px-3.5 py-3 hover:bg-app-grey-4 transition-colors",
        !isLast && "!border-b !border-solid !border-app-grey-3",
      )}
    >
      <span className="text-sm font-inter text-app-grey-0">{displayText}</span>
      <IconButton
        src="/assets/delete.svg"
        size={28}
        iconSize={18}
        aria-label={`Delete "${displayText}"`}
        onClick={onDelete}
        className="opacity-0 group-hover:opacity-100 transition-opacity [&_path]:fill-app-grey-1 hover:[&_path]:fill-app-grey-0 flex-shrink-0"
      />
    </div>
  );
};
